import { Button } from "@/components/ui/button";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, Menu } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const { data: userData, refetch: refetchUser } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      return {
        id: user.id,
        email: user.email,
        first_name: user.user_metadata?.first_name || '',
        last_name: user.user_metadata?.last_name || '',
      };
    },
  });

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        refetchUser();
      } else {
        refetchUser();
      }
    });

    return () => subscription.unsubscribe();
  }, [refetchUser]);

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate("/");
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error signing out",
        description: error.message,
      });
    }
  };

  const NavLinks = () => (
    <>
      <Link 
        to="/" 
        className={location.pathname === "/" ? "text-[#059669]" : "text-[#374151]"}
        onClick={() => setIsMobileMenuOpen(false)}
      >
        Home
      </Link>
      <Link 
        to="/about" 
        className={location.pathname === "/about" ? "text-[#059669]" : "text-[#374151]"}
        onClick={() => setIsMobileMenuOpen(false)}
      >
        About
      </Link>
      <Link 
        to="/how-it-works" 
        className={location.pathname === "/how-it-works" ? "text-[#059669]" : "text-[#374151]"}
        onClick={() => setIsMobileMenuOpen(false)}
      >
        How it works
      </Link>
    </>
  );

  const AuthSection = ({ isMobile = false }: { isMobile?: boolean }) => {
    if (userData) {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className={`bg-[#059669] hover:bg-[#059669]/90 ${isMobile ? 'mt-6 w-full' : ''}`}>
              Hello {userData.first_name}
              <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-48">
            <DropdownMenuItem asChild>
              <Link to="/submission" className="w-full cursor-pointer">
                Go to Submission Form
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/report" className="w-full cursor-pointer">
                View Report
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/account" className="w-full cursor-pointer">
                My Account
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem 
              className="cursor-pointer text-red-600 focus:text-red-600"
              onClick={handleSignOut}
            >
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }
    
    return (
      <Button 
        className={`bg-[#059669] hover:bg-[#059669]/90 ${isMobile ? 'mt-6 w-full' : ''}`}
        asChild
        onClick={() => setIsMobileMenuOpen(false)}
      >
        <Link to="/auth">Sign In</Link>
      </Button>
    );
  };

  return (
    <header className="w-full h-20 bg-white shadow-sm">
      <div className="container mx-auto h-full px-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="h-8">
            <img 
              src="/lovable-uploads/2493eb09-6862-4509-bab3-205f0ffe4dc3.png" 
              alt="Kosi Logo" 
              className="h-full"
            />
          </Link>
          <span className="text-[#9CA3AF]">|</span>
          <span className="text-lg text-[#4B5563]">Understand Your Energy</span>
        </div>
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex flex-1 items-center justify-center">
          <nav className="flex items-center gap-8">
            <NavLinks />
          </nav>
        </div>

        {/* Desktop Auth Section */}
        <div className="hidden md:block">
          <AuthSection />
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden">
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px]">
              <nav className="flex flex-col space-y-6 mt-8">
                <NavLinks />
                <AuthSection isMobile />
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;